import { attributionResponse } from './attribution.js';
import { bookingFunnelResponse } from './booking-funnel.js';
import * as e from './endpoints.js';
import { growthSummaryResponse } from './growth.js';
import { healthResponse, okResponse } from './health.js';
import { landingV2Response } from './landing.js';
import { leadsResponse } from './leads.js';
import { metaCustomersResponse } from './meta-customers.js';
import { metaAdPreviewResponse, metaLayoutResetResponse, metaLayoutResponse, metaLayoutSaveResponse, metaStructureResponse, } from './meta-structure.js';
import { quizResponse } from './quiz.js';
import { referralCreateResponse, referralStatusResponse, referralsResponse, } from './referrals.js';
import { socialDecideResponse, socialDraftsResponse, socialRunResponse, } from './social.js';
import { truthRefreshResponse, truthResponse } from './truth.js';
/** Thrown on any non-2xx response so callers (TanStack Query) can branch on 401/403. */
export class ApiError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.status = status;
        this.name = 'ApiError';
    }
}
function qs(params) {
    const u = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '')
            u.set(k, String(v));
    }
    const s = u.toString();
    return s ? `?${s}` : '';
}
/**
 * One typed client over /api/dashboard. Every method validates the response with
 * the shared Zod schema, so a server-side shape drift throws here (in the SPA)
 * rather than silently rendering wrong data.
 */
export function createApiClient(cfg) {
    const base = cfg.baseUrl ?? '/api/dashboard';
    async function req(path, schema, init) {
        const headers = new Headers(init?.headers);
        const token = cfg.getToken();
        if (token)
            headers.set('Authorization', `Bearer ${token}`);
        if (init?.body)
            headers.set('Content-Type', 'application/json');
        // same-origin: send the HttpOnly session cookie (the SPA is served by the
        // API, so this never leaks the cookie cross-site).
        const res = await fetch(base + path, { ...init, headers, credentials: 'same-origin' });
        if (!res.ok) {
            let msg = res.statusText;
            try {
                const j = (await res.json());
                if (typeof j.error === 'string')
                    msg = j.error;
            }
            catch {
                /* non-JSON error body */
            }
            throw new ApiError(res.status, msg);
        }
        return schema.parse(await res.json());
    }
    return {
        config: () => req('/config', e.configResponse),
        me: () => req('/me', e.meResponse),
        createSession: (credential) => req('/session', e.sessionResponse, { method: 'POST', body: JSON.stringify({ credential }) }),
        deleteSession: () => req('/session', e.sessionDeleteResponse, { method: 'DELETE' }),
        freshness: () => req('/freshness', e.freshnessResponse),
        metrics: (r) => req(`/metrics${qs(r)}`, e.metricsResponse),
        funnel: (r) => req(`/funnel${qs(r)}`, e.funnelResponse),
        ads: (r) => req(`/ads${qs(r)}`, e.adsResponse),
        attribution: (r) => req(`/attribution${qs({ from: r.from, to: r.to })}`, attributionResponse),
        quiz: (r) => req(`/quiz${qs({ from: r.from, to: r.to })}`, quizResponse),
        bookingFunnel: (r) => req(`/booking-funnel${qs({ from: r.from, to: r.to })}`, bookingFunnelResponse),
        leads: (r) => req(`/leads${qs({ from: r.from, to: r.to })}`, leadsResponse),
        metaCustomers: (r) => req(`/meta/customers${qs({ from: r.from, to: r.to })}`, metaCustomersResponse),
        metaStructure: (r) => req(`/meta/structure${qs({ from: r?.from, to: r?.to, fresh: r?.fresh ? 1 : undefined })}`, metaStructureResponse),
        metaLayout: () => req('/meta/layout', metaLayoutResponse),
        metaLayoutSave: (positions) => req('/meta/layout', metaLayoutSaveResponse, {
            method: 'PUT',
            body: JSON.stringify({ positions }),
        }),
        metaLayoutReset: () => req('/meta/layout', metaLayoutResetResponse, { method: 'DELETE' }),
        socialDrafts: (status) => req(`/social/drafts${qs({ status })}`, socialDraftsResponse),
        socialDecide: (id, body) => req(`/social/drafts/${id}`, socialDecideResponse, { method: 'PATCH', body: JSON.stringify(body) }),
        socialRun: () => req('/social/runs', socialRunResponse, { method: 'POST' }),
        metaAdPreview: (adId, format) => req(`/meta/ad/${encodeURIComponent(adId)}/preview${qs({ format })}`, metaAdPreviewResponse),
        truth: (r) => req(`/truth${qs({ from: r?.from, to: r?.to })}`, truthResponse),
        truthRefresh: () => req('/truth/refresh', truthRefreshResponse, { method: 'POST' }),
        growthSummary: (r) => req(`/growth-summary${qs({ from: r?.from, to: r?.to })}`, growthSummaryResponse),
        healthCheck: (r) => req(`/health-check${qs({ from: r?.from, to: r?.to })}`, healthResponse),
        healthExclude: (body) => req('/health/exclude', okResponse, { method: 'POST', body: JSON.stringify(body) }),
        referrals: (days) => req(`/referrals${qs({ days })}`, referralsResponse),
        referralCreate: (body) => req('/referrals', referralCreateResponse, { method: 'POST', body: JSON.stringify(body) }),
        referralStatus: (id, body) => req(`/referrals/${id}/status`, referralStatusResponse, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        planRates: (lookback) => req(`/plan-rates${qs({ lookback })}`, e.planRatesResponse),
        landing: (r) => req(`/landing${qs({ from: r.from, to: r.to, handle: r.handle })}`, landingV2Response),
        targets: () => req('/targets', e.targetsResponse),
        putTarget: (body) => req('/targets', e.putTargetsResponse, { method: 'PUT', body: JSON.stringify(body) }),
        rules: () => req('/rules', e.rulesResponse),
        patchRule: (id, body) => req(`/rules/${id}`, e.rulesResponse, { method: 'PATCH', body: JSON.stringify(body) }),
        alerts: () => req('/alerts', e.alertsResponse),
        refresh: (body) => req('/refresh', e.refreshResponse, { method: 'POST', body: JSON.stringify(body ?? {}) }),
    };
}
//# sourceMappingURL=client.js.map